/* TrialBanner — global Trial countdown + expired blocked overlay.
 *
 * Reads the authenticated user's live trial status (from /auth/me → user.trial)
 * and:
 *   • shows a slim, non-intrusive countdown banner with an Upgrade CTA while
 *     the trial is active (emphasis grows as expiry nears), and
 *   • renders a full blocking overlay once the trial has expired — normal app
 *     content is covered, but the Upgrade flow (existing UpgradePlanDialog)
 *     stays reachable so the org can convert and continue.
 *
 * Reuses the existing UpgradePlanDialog; introduces no new upgrade mechanism.
 */
import React, { useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { UpgradePlanDialog } from "@/components/billing/UpgradePlanDialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, Rocket } from "lucide-react";

export default function TrialBanner() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const trial = user?.trial;
    if (!trial || !trial.is_trial) return null;

    const dialog = <UpgradePlanDialog open={open} onOpenChange={setOpen} currentPlanName="Trial" />;

    // ---- Expired → full blocking overlay (upgrade still reachable) --------
    if (trial.expired) {
        return (
            <>
                <div
                    className="fixed inset-0 z-[200] bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="trial-expired-overlay"
                >
                    <div className="max-w-md w-full bg-card border border-border rounded-md shadow-lg p-6 text-center">
                        <div className="mx-auto w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                        </div>
                        <h2 className="text-lg font-semibold mb-1.5">Your 14-day trial has ended</h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            Please upgrade to a Standard or Enterprise plan to continue using Analyzio.
                            Your data, dashboards and configuration are safe.
                        </p>
                        <Button onClick={() => setOpen(true)} data-testid="trial-expired-upgrade-btn">
                            <Rocket className="h-4 w-4 mr-1.5" /> Upgrade Plan
                        </Button>
                    </div>
                </div>
                {dialog}
            </>
        );
    }

    // ---- Active → slim countdown banner -----------------------------------
    const days = trial.days_remaining;
    const urgent = typeof days === "number" && days <= 3;
    const label =
        days === 0 ? "Your trial expires today"
            : days === 1 ? "Your trial expires tomorrow"
                : `Your trial has ${days} days remaining`;

    return (
        <>
            <div
                className={`flex items-center justify-center gap-3 px-4 py-1.5 text-xs border-b ${urgent ? "bg-destructive/10 border-destructive/30 text-foreground" : "bg-primary/10 border-primary/20 text-foreground"}`}
                data-testid="trial-banner"
            >
                <Clock className={`h-3.5 w-3.5 ${urgent ? "text-destructive" : "text-primary"}`} />
                <span data-testid="trial-banner-text">
                    <b>Trial</b> · {label}. Upgrade to Standard or Enterprise to keep your dashboards and analytics without interruption.
                </span>
                <Button size="sm" variant={urgent ? "default" : "outline"} className="h-6 px-2 text-[11px] rounded-sm"
                    onClick={() => setOpen(true)} data-testid="trial-banner-upgrade-btn">
                    Upgrade Plan
                </Button>
            </div>
            {dialog}
        </>
    );
}
